/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable } from '@angular/core';

/**
 * Makes the sidenav sub-menus (FILES / SAVED SEARCHES / Admin Tools) collapsible.
 *
 * ACA hard-codes `[expanded]="true"` on its `<mat-expansion-panel>` groups, so
 * clicking the header can't fold them — Angular re-forces the open state on the
 * next change-detection pass. We instead decouple from Material's state and drive
 * open/closed purely from our own `.aca-submenu-open` class, toggled here on a
 * header click. The matching CSS (in `_modern.scss`) keeps the body collapsed by
 * default and reveals it when the class is present (or a child route is active).
 */
@Injectable({ providedIn: 'root' })
export class SubmenuToggleService {
  private bound = false;

  init(): void {
    if (this.bound || typeof document === 'undefined') {
      return;
    }
    this.bound = true;

    // Capture phase: runs BEFORE any inner Material/ACA handler that may call
    // stopPropagation() on the header button — otherwise our toggle never fires.
    document.addEventListener(
      'click',
      (ev) => {
        const target = ev.target as HTMLElement | null;
        const header = target?.closest('.aca-expansion-panel-header');
        // Only react to header clicks inside the app sidenav.
        if (!header?.closest('.aca-sidenav')) {
          return;
        }
        header.closest('.aca-expansion-panel')?.classList.toggle('aca-submenu-open');
      },
      true
    );

    // Sub-item click reliability. A clipping ancestor between the sub-item and the
    // panel makes Chromium's hit-test resolve a real mouse click on a deep sub-item
    // to the ancestor `mat-expansion-panel` instead of the item — so it never
    // navigates (DOM `.click()` works; only the pointer hit-test is wrong). Verified
    // still present after removing the app `zoom` (zoom was NOT the cause). Fallback:
    // if a sidenav click did NOT land on an item/header, find the visible sub-item
    // whose box contains the pointer and invoke it ourselves.
    document.addEventListener(
      'click',
      (ev) => {
        const target = ev.target as HTMLElement | null;
        if (!target?.closest('.aca-sidenav')) {
          return;
        }
        if (target.closest('.aca-expansion-panel-body .aca-action-button') || target.closest('.aca-expansion-panel-header')) {
          return; // already routed to an item or header
        }
        const items = Array.from(document.querySelectorAll<HTMLElement>('.aca-sidenav .aca-expansion-panel-body .aca-action-button'));
        const hit = items.find((it) => {
          if (!it.getClientRects().length) {
            return false; // in a collapsed (display:none) panel
          }
          const r = it.getBoundingClientRect();
          return ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
        });
        if (hit) {
          ev.preventDefault();
          ev.stopPropagation();
          hit.click();
        }
      },
      true
    );
  }
}
